import { FacilityParams } from './facility-params'
import { Hold } from './hold-params'

/**
 * One row of the Facilities grid, exactly as `GET /api/facility-management/screen`
 * returns it: the columns the table renders, with the deal and admin names,
 * the holds, the non-pro-rata flag and whether lenders hold positions already
 * resolved, and an amendment in flight shown in place of the approved
 * facility it amends.
 *
 * The property names are the ones the grid, its KPI strip and its row menu
 * already read off a `FacilityParams`, so they run unchanged. It is not a
 * whole facility: the forms behind the row menu fetch the one facility they
 * act on, when they open.
 */
export interface FacilityScreenRow {
  id: string
  status: 'Draft' | 'Submitted' | 'Approved' | 'Terminated'
  /** True when the row is an amendment in flight standing in for the approved facility. */
  amendment: boolean
  /** An amendment's own id; the row menu opens and deletes the amendment by it. */
  transactionId?: string
  amendmentType: string | null
  amendmentDate: string | null
  /** For an amendment row: whether it carries its own lender allocations. */
  hasAmendmentLenders: boolean
  name: string
  facilityType: string
  amount: string | null
  utilizedAmount: string
  baseCurrency: string
  startDate: string | null
  maturity: string | null
  /** The deal's key; `admin` is set when the read had it (the SP deal hold cascades by it). */
  dealId: { id: string; admin?: string }
  dealName: string
  adminEntity: { id: string } | null
  adminEntityName: string
  clientId: string
  /** Assigned on approval: '' on a draft. Hidden column, CSV export only. */
  systemId: string
  /** The facility's key admin: the bulk submit / approve endpoints address a facility by it. */
  accountManagementAdmin: string | null
  amortizationScheduleId: { id: string } | null
  /** The ledger's holds plus the Signing Portal's. */
  holds: Hold[]
  hasNonProRataLoans: boolean
  /** For a terminated facility: when it was locked. */
  terminationDate: string | null
  /**
   * On an amendment row: the approved facility's own values, shown again once
   * the amendment is deleted — without a re-read. Null on a facility's own row.
   */
  approved: FacilityScreenApprovedValues | null
}

export interface FacilityScreenApprovedValues {
  name: string
  facilityType: string
  amount: string | null
  utilizedAmount: string
  baseCurrency: string
  startDate: string | null
  maturity: string | null
  clientId: string
  /** An amendment may name another admin; deleting it brings this one back. */
  adminEntity: { id: string } | null
  adminEntityName: string
}

/**
 * The table's column definitions, the row menu and the KPI strip are typed on
 * `FacilityParams`; a screen row carries the fields they read. This is a type
 * change only — nothing is fetched.
 */
export const facilityScreenRowsAsParams = (rows: FacilityScreenRow[]): FacilityParams[] =>
  rows as unknown as FacilityParams[]
